import { useCallback, useEffect, useRef, useState } from "react";

import { useRemoteControl, type RegisteredSidebar } from "@dexibit/core";
import { useNavigate } from "react-router-dom";

import { LogoMark } from "../../assets/logoMark";
import { LogoText } from "../../assets/logoText";
import { useAppSelector } from "../../store";
import { useLazyCheckAuthenticationQuery } from "../../store/services/authentication";
import { selectRedirectUri, useGetConfigQuery } from "../../store/services/config";
import type { UserInfoDTO } from "../../store/services/userInfo/types";
import type { ActiveLink, NavigationLink, SubLink } from "../../types";
import { redirectToAuthorise } from "../../utils/oauth";
import { isMobileViewport } from "./constants";
import { MobileBar } from "./MobileBar";
import { NavItem } from "./NavItem";
import { NavSupport } from "./NavSupport";
import { NavUserFooter } from "./NavUserFooter";
import {
  Backdrop,
  HomeItemWrapper,
  LogoContainer,
  LogoIconArea,
  NavDivider,
  NavGroup,
  NavSpacer,
  SidebarContent,
  StyledNav,
  StyledOverlay,
} from "./styles";
import { useNavigation } from "./useNavigation";

interface NavigationProps {
  links: NavigationLink[];
  activeLink?: ActiveLink | undefined;
  dialogVisible?: boolean;
  userInfo: UserInfoDTO | undefined;
  accountName: string | undefined;
  venueName: string | undefined;
}

export const Navigation = ({
  links,
  activeLink,
  dialogVisible,
  userInfo,
  accountName,
  venueName,
}: NavigationProps) => {
  const navigate = useNavigate();
  const { data: config } = useGetConfigQuery();
  const redirectUri = useAppSelector(selectRedirectUri);
  const [checkAuth, results] = useLazyCheckAuthenticationQuery();

  const { collapseSidebar, expandSidebar, sidebars } = useRemoteControl();

  const [visibleSidebar, setVisibleSidebar] = useState<RegisteredSidebar | undefined>();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isMobileViewport);
  const sidebarsAtNavRef = useRef(sidebars);

  // On navigation: snapshot sidebars and clear
  useEffect(() => {
    sidebarsAtNavRef.current = sidebars;
    setVisibleSidebar(undefined);
    setIsSidebarCollapsed(isMobileViewport());
  }, [activeLink?.to]);

  // Only accept sidebar registrations that arrive after the last navigation (new reference)
  useEffect(() => {
    if (sidebars !== sidebarsAtNavRef.current) {
      setVisibleSidebar(sidebars.find((s) => s.position === "left"));
    }
  }, [sidebars]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (typeof event.data !== "object" || event.data === null) return;
      const { type, position } = event.data;
      if (position !== "left") return;
      if (type === "PANEL_SIDEBAR_COLLAPSE") {
        setIsSidebarCollapsed(true);
      } else if (type === "PANEL_SIDEBAR_EXPAND") {
        setIsSidebarCollapsed(false);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const toggleSidebar = useCallback(() => {
    if (!visibleSidebar) return;
    if (isSidebarCollapsed) {
      expandSidebar("left");
    } else {
      collapseSidebar("left");
    }
    setIsSidebarCollapsed((prev) => !prev);
  }, [visibleSidebar, isSidebarCollapsed, collapseSidebar, expandSidebar]);

  const askLink = links.find((link) => link.title === "Ask");
  const knowledgeLink = links.find((link) => link.title === "Knowledge");
  const forecastsLink = links.find((link) => link.title === "Forecasts");
  const adminLink = links.find((link) => link.title === "Admin");
  const legacyLink = links.find((link) => link.title === "Legacy");
  const moduleLinks = links.filter(
    (link) =>
      link.title !== "Admin" &&
      link.title !== "Ask" &&
      link.title !== "Knowledge" &&
      link.title !== "Forecasts" &&
      link.title !== "Legacy",
  );

  const {
    isHovered,
    isFocusWithin,
    isMobileDrawerOpen,
    expandedAccordions,
    onMouseEnter,
    onMouseLeave,
    onSidebarFocus,
    onSidebarBlur,
    toggleMobileDrawer,
    closeMobileDrawer,
    toggleAccordion,
    openAccordion,
  } = useNavigation();

  const isExpanded = isHovered || isFocusWithin || isMobileDrawerOpen;

  const onNavigationClick = (link: NavigationLink | SubLink) => {
    if (!config) return;
    void checkAuth({ verifyTokenUri: config.verifyTokenUri });
    const options = link.iFrameUrl ? { state: { iFrameUrl: link.iFrameUrl } } : undefined;
    navigate(link.to, options);
    closeMobileDrawer();
    onMouseLeave();
    (document.activeElement as HTMLElement | null)?.blur();
  };

  useEffect(() => {
    const { isUninitialized, isFetching, isError } = results;
    if (!isUninitialized && !isFetching && isError && redirectUri) {
      redirectToAuthorise(redirectUri);
    }
  }, [results, redirectUri]);

  return (
    <>
      <MobileBar
        isDrawerOpen={isMobileDrawerOpen}
        onToggleDrawer={toggleMobileDrawer}
        leftSidebar={visibleSidebar}
        isSidebarCollapsed={isSidebarCollapsed}
        onToggleSidebar={toggleSidebar}
      />
      {isMobileDrawerOpen && <Backdrop onClick={closeMobileDrawer} />}
      <StyledNav aria-label="Main navigation">
        <SidebarContent
          isExpanded={isExpanded}
          isMobileOpen={isMobileDrawerOpen}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onFocus={onSidebarFocus}
          onBlur={onSidebarBlur}
        >
          <LogoContainer>
            <LogoIconArea>
              <LogoMark />
            </LogoIconArea>
            {isExpanded && <LogoText />}
          </LogoContainer>
          {askLink && (
            <HomeItemWrapper>
              <NavItem
                link={askLink}
                activeLink={activeLink}
                isExpanded={isExpanded}
                isAccordionOpen={false}
                onToggleAccordion={() => {}}
                onOpenAccordion={() => {}}
                onNavigate={onNavigationClick}
              />
              {knowledgeLink && (
                <NavItem
                  link={knowledgeLink}
                  activeLink={activeLink}
                  isExpanded={isExpanded}
                  isAccordionOpen={false}
                  onToggleAccordion={() => {}}
                  onOpenAccordion={() => {}}
                  onNavigate={onNavigationClick}
                />
              )}
              {forecastsLink && (
                <NavItem
                  link={forecastsLink}
                  activeLink={activeLink}
                  isExpanded={isExpanded}
                  isAccordionOpen={false}
                  onToggleAccordion={() => {}}
                  onOpenAccordion={() => {}}
                  onNavigate={onNavigationClick}
                />
              )}
            </HomeItemWrapper>
          )}
          <NavGroup>
            {moduleLinks.map((link) => (
              <NavItem
                key={link.title}
                link={link}
                activeLink={activeLink}
                isExpanded={isExpanded}
                isAccordionOpen={expandedAccordions.has(link.to)}
                onToggleAccordion={() => toggleAccordion(link.to)}
                onOpenAccordion={() => openAccordion(link.to)}
                onNavigate={onNavigationClick}
              />
            ))}
          </NavGroup>
          {legacyLink && (
            <NavGroup>
              <NavItem
                link={legacyLink}
                activeLink={activeLink}
                isExpanded={isExpanded}
                isAccordionOpen={expandedAccordions.has(legacyLink.to)}
                onToggleAccordion={() => toggleAccordion(legacyLink.to)}
                onOpenAccordion={() => openAccordion(legacyLink.to)}
                onNavigate={onNavigationClick}
              />
            </NavGroup>
          )}
          {adminLink && (
            <>
              <NavDivider />
              <NavGroup>
                <NavItem
                  link={adminLink}
                  activeLink={activeLink}
                  isExpanded={isExpanded}
                  isAccordionOpen={expandedAccordions.has(adminLink.to)}
                  onToggleAccordion={() => toggleAccordion(adminLink.to)}
                  onOpenAccordion={() => openAccordion(adminLink.to)}
                  onNavigate={onNavigationClick}
                />
              </NavGroup>
            </>
          )}
          <NavSpacer />
          <NavSupport isExpanded={isExpanded} />
          <NavUserFooter
            userInfo={userInfo}
            accountName={accountName}
            venueName={venueName}
            isExpanded={isExpanded}
          />
        </SidebarContent>
        {dialogVisible && <StyledOverlay isExpanded={isExpanded} />}
      </StyledNav>
    </>
  );
};
